import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, unwrap } from "./client";
import { jdQuestionKeys } from "./jd-question-queries";
import { jdKeys } from "./jd-queries";
import type { components } from "./schema";

/**
 * Job descriptions, Phase 3 — the gap report, questions drafted for a gap, and skill aliases (DESIGN
 * §20.6, §20.11). Same conventions as jd-queries.ts: generated types, `unwrap`, prefix invalidation.
 */

type Schemas = components["schemas"];
export type GapReport = Schemas["GapReportOut"];
export type Gap = Schemas["GapOut"];
export type GapJD = Schemas["GapJDOut"];
export type JDDrafts = Schemas["JDDraftsOut"];
export type JDDraftBatch = Schemas["JDDraftBatchOut"];
export type Aliases = Schemas["AliasesOut"];
export type Alias = Schemas["AliasOut"];

export const gapKeys = {
  report: ["jd-gaps"] as const,
  drafts: (id: number) => ["jds", "drafts", id] as const,
  aliases: (retired: boolean) => ["jd-aliases", retired] as const,
};

export function useGapReport() {
  return useQuery({
    queryKey: gapKeys.report,
    queryFn: async () => unwrap(await api.GET("/jd-gaps", {})),
  });
}

/** Queues `jd_generate`; the drafts arrive on the JD's Questions tab, which polls for them. */
export function useGenerateForGap(jdId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { skill_id: number; count: number; stack: string | null }) =>
      unwrap(await api.POST("/jds/{jd_id}/generate", { params: { path: { jd_id: jdId } }, body })),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: gapKeys.drafts(jdId) });
      void client.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

/**
 * Polls while a `jd_generate` job for the JD is still writing: `pending` is how many are queued or
 * running, which the caller knows from the jobs it started.
 */
export function useJDDrafts(jdId: number, pending: boolean) {
  return useQuery({
    queryKey: gapKeys.drafts(jdId),
    queryFn: async () => unwrap(await api.GET("/jds/{jd_id}/drafts", { params: { path: { jd_id: jdId } } })),
    refetchInterval: pending ? 4_000 : false,
  });
}

export function useReviewJDDrafts(jdId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, keepIds }: { jobId: number; keepIds: number[] }) =>
      unwrap(
        await api.POST("/jds/{jd_id}/drafts/{job_id}/review", {
          params: { path: { jd_id: jdId, job_id: jobId } },
          body: { keep_ids: keepIds },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: gapKeys.drafts(jdId) });
      void client.invalidateQueries({ queryKey: jdQuestionKeys.questions(jdId) });
      void client.invalidateQueries({ queryKey: jdKeys.all });
      void client.invalidateQueries({ queryKey: gapKeys.report });
    },
  });
}

export function useAliases(includeRetired: boolean) {
  return useQuery({
    queryKey: gapKeys.aliases(includeRetired),
    queryFn: async () =>
      unwrap(await api.GET("/jd-aliases", { params: { query: { include_retired: includeRetired } } })),
  });
}

function useAliasWrite<T>(write: (input: T) => Promise<Aliases>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: () => void client.invalidateQueries({ queryKey: ["jd-aliases"] }),
  });
}

export function useAddAlias() {
  return useAliasWrite(
    async (body: { words: string; category: "technical" | "domain"; stack_path: string }) =>
      unwrap(await api.POST("/jd-aliases", { body })),
  );
}

export function useRepointAlias() {
  return useAliasWrite(async ({ id, stackPath }: { id: number; stackPath: string }) =>
    unwrap(
      await api.PATCH("/jd-aliases/{alias_id}", {
        params: { path: { alias_id: id } },
        body: { stack_path: stackPath },
      }),
    ),
  );
}

export function useRetireAlias() {
  return useAliasWrite(async (id: number) =>
    unwrap(await api.DELETE("/jd-aliases/{alias_id}", { params: { path: { alias_id: id } } })),
  );
}
